import { Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { LabelType } from '../../../elements/components/label/entities/label-type.entity';
import { Comparable, Trackable } from '../../../helpers/public_api';
import { ApiJob } from '../../jobs/entities/api-job.entity';
import { User } from '../../users/entities/user.entity';
import { SearchRequest, ShBatchTaskTypes } from './batch-options.class';
import { BatchTask } from './batch-tasks.entity';

/**
 * Batch object that is made on sending a BatchOptions object to the /batches endpoint. Queryable by GETting the Id at /batches/:id
 *
 * Batches operate on a large data set conveyed via a filter. The data set is then linked to a one of various tasks. After creation, the status of the batch can be monitored.
 *  @see https://mh-tst.mediahaven.com/mediahaven-rest-api/v2/api-docs/index.html#mediahaven-rest-api-manual-batches
 */
export interface BatchObject {
    Id: string;
    UserId: string;
    SearchRequest: SearchRequest;
    Active: number;
    Completed: number;
    Skipped: number;
    Failed: number;
    Total: number;
    Slices: number;
    CreationDate: Date | null;
    StartDate: Date | null;
    FinishDate: string | null;
    Estimate: Date | null;
    Label: string;
    TaskType: ShBatchTaskTypes;
    Indices: string[];
    Status: BatchStatus;
    Task: BatchTask;
    Tag: string;
}

export enum JobStatus {
    IN_PROGRESS = 'InProgress',
    PROCESSING = 'Processing',
    COMPLETED = 'Completed',
    FAILED = 'Failed',
    WAITING = 'Waiting',
    INTERRUPTED = 'Interrupted',
    REJECTED = 'Rejected'
}

export enum BatchStatus {
    WAITING = 'Waiting',
    PROCESSING = 'Processing',
    POST_BATCH_FAILED = 'PostBatchFailed',
    COMPLETED_WITH_ERRORS = 'CompletedWithErrors',
    COMPLETED = 'Completed',
    CANCELED = 'Cancelled',
    TOO_MANY_FAILED = 'TooManyFailed'
}

export class PaginatedBatch implements BatchObject, Comparable, Trackable {
    Id: string;
    UserId: string;
    SearchRequest: SearchRequest;
    Active: number;
    Completed: number;
    Skipped: number;
    Failed: number;
    Total: number;
    Slices: number;
    CreationDate: Date | null;
    StartDate: Date | null;
    FinishDate: string | null;
    Estimate: Date | null;
    Label: string;
    TaskType: ShBatchTaskTypes;
    Indices: string[];
    Status: BatchStatus;
    Task: BatchTask;
    Tag: string;
    User$: Observable<User | null>;
    Jobs$: Observable<ApiJob[]> = of([]);
    Status$: Observable<JobStatus>;

    constructor(batchObject: BatchObject, jobs$: Observable<ApiJob[]>, user$: Observable<User | null>) {
        Object.assign(this, batchObject);

        this.Jobs$ = jobs$;
        this.User$ = user$;

        this.initStatus();
    }

    public trackBy(): string {
        return this.Id;
    }

    public equals(other: PaginatedBatch): boolean {
        if (!(other instanceof PaginatedBatch)) {
            return false;
        }
        return this.Id === other.Id;
    }

    // Must be set like this because LabelType$ could cause multiple fetches otherwise
    private initStatus(): void {
        if (!this.StartDate) {
            this.Status$ = of(JobStatus.WAITING);
            return;
        }
        if (this.Active > 0) {
            this.Status$ = this.Jobs$.pipe(
                map(jobs =>
                    jobs.length > 0 && jobs.some(job => !!job.Error && job.Error.length > 0)
                        ? JobStatus.INTERRUPTED
                        : JobStatus.IN_PROGRESS
                ),
                shareReplay(1) // prevent LabelType$ to trigger a new fetch
            );
            return;
        }
        if (this.Failed > 0) {
            this.Status$ = of(JobStatus.FAILED);
            return;
        }
        this.Status$ = of(JobStatus.COMPLETED);
    }

    public get LabelType$(): Observable<LabelType> {
        return this.Status$.pipe(
            map(status => {
                switch (status) {
                    case JobStatus.WAITING:
                        return LabelType.Default;
                    case JobStatus.IN_PROGRESS:
                        return LabelType.Update;
                    case JobStatus.FAILED:
                    case JobStatus.INTERRUPTED:
                        return LabelType.Error;
                    default:
                        return LabelType.Success;
                }
            })
        );
    }

    public get Progress(): number {
        if (this.Total === 0) return 100;
        return ((this.Completed + this.Skipped + this.Failed) / this.Total) * 100;
    }

    public get ProgressLabel(): string {
        return `${this.Progress}%`;
    }
}

export const emptyBatchObject: BatchObject = {
    Id: '',
    UserId: '',
    SearchRequest: {
        Query: ''
    },
    Active: 0,
    Completed: 0,
    Skipped: 0,
    Failed: 0,
    Total: 0,
    Slices: 0,
    CreationDate: null,
    StartDate: null,
    FinishDate: null,
    Estimate: null,
    Label: '',
    TaskType: ShBatchTaskTypes.MERGE_MULTIPLE,
    Indices: [],
    Status: BatchStatus.COMPLETED,
    Task: {},
    Tag: ''
};
